import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listCalendar, saveCalendarItem, deleteCalendarItem } from "@/lib/journey.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/journey/calendar")({
  component: CalendarPage,
});

type Item = Awaited<ReturnType<typeof listCalendar>>[number];

function CalendarPage() {
  const load = useServerFn(listCalendar);
  const save = useServerFn(saveCalendarItem);
  const del = useServerFn(deleteCalendarItem);
  const [items, setItems] = useState<Item[]>([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [starts, setStarts] = useState<string>(() => new Date().toISOString().slice(0, 16));

  const refresh = () => load({ data: {} }).then(setItems).catch(() => {});
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleSave = async () => {
    if (!title.trim()) return;
    try {
      await save({ data: { title, notes, starts_at: new Date(starts).toISOString() } });
      setOpen(false); setTitle(""); setNotes("");
      refresh();
    } catch (e) { toast.error((e as Error).message); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Remove this calendar item?")) return;
    await del({ data: { id } }); refresh();
  };

  const upcoming = items.filter((i) => new Date(i.starts_at) >= new Date(Date.now() - 24 * 3600 * 1000));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground/70">Personal practice plans, reminders, and dates that matter to you.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus size={14} className="mr-1" />Add item</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New calendar item</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
              <Input type="datetime-local" value={starts} onChange={(e) => setStarts(e.target.value)} />
              <Textarea placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
              <Button onClick={handleSave}>Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4">
        <h2 className="mb-3 font-serif text-lg text-gold-luminous">Upcoming</h2>
        {upcoming.length === 0 && <p className="text-sm text-foreground/60">Nothing scheduled yet.</p>}
        <ul className="space-y-2">
          {upcoming.map((i) => (
            <li key={i.id} className="flex items-start justify-between rounded-md border hairline p-3">
              <div>
                <div className="text-sm font-medium">{i.title}</div>
                <div className="text-xs text-foreground/60">{new Date(i.starts_at).toLocaleString()}</div>
                {i.notes && <div className="mt-1 text-sm text-foreground/70">{i.notes}</div>}
              </div>
              <Button variant="ghost" size="icon" aria-label="Delete" onClick={() => handleDelete(i.id)}>
                <Trash2 size={14} />
              </Button>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
